import { Button, Container, Grid } from '@mui/material';
import EventFilesSection from 'components/events/EventFilesSection';
import EventLocationRow from 'components/events/EventInfoRow/EventLocationRow';
import EventTimeRow from 'components/events/EventInfoRow/EventTimeRow';
import ManageEventSessionsModal from 'components/events/ManageEventSessionsModal';
import AddToWaitListModal from 'components/events/AddToWaitListModal';
import Card from 'components/shared/Card';
import EventDateRow from 'components/events/EventInfoRow/EventCalendarIcon';
import { UserContext } from 'components/UserGuard';
import { useSnackbar } from 'notistack';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import eventModeConfig from 'store/configs/EventModeConfig';
import { EventView } from 'store/types/Event';
import { EVENT_DATE_FORMAT, getEventModeIcon, getEventPriceLevel, getTotalCredits } from 'util/Event';
import { EMPTY_STRING_VALUE } from 'util/Format';
import { defaultGridContainerProps, defaultGridItemProps, getButtonLoadingProps } from 'util/Layout';
import { getPrice } from 'util/Payment';
import ManageCourseSessionsMode from 'store/enums/ManageCourseSessionsMode';
import Check from '@mui/icons-material/Check';
import EventService from 'services/api/EventService';
import { EventCartContext } from 'components/EventCartGuard';
import { getLoginPath } from 'util/Route';
import { useLocation } from 'react-router';
import SchoolOutlined from '@mui/icons-material/SchoolOutlined';
import classNames from 'classnames';
import StatusLabel from 'components/shared/StatusLabel';
import { ParametersContext } from 'components/ParametersGuard';

import commonStyles from 'styles/common.module.scss';
import styles from './EventDetailsPageView.module.scss';
import rowStyles from '../EventInfoRow/EventInfoRow.module.scss';

interface EventDetailsPageViewProps {
  data: EventView;
}

const EventDetailsPageView: React.FunctionComponent<EventDetailsPageViewProps> = ({ data }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { pathname } = useLocation();
  const currentUser = useContext(UserContext);
  const {
    events: { multiSessionLabelName },
  } = useContext(ParametersContext);
  const { cart = [], refetchCart, cartLoading } = useContext(EventCartContext);
  const hasAuthorizedUser: boolean = useMemo(() => !!currentUser.id, [currentUser]);
  const priceLevelId: string = useMemo(() => currentUser.priceLevelId, [currentUser]);
  const {
    id,
    title,
    mode,
    description = '',
    imageUrl,
    material,
    availableForSale,
    sessions,
    endDate,
    startDate,
    availableUntilDate,
    memberTypes,
    prices,
    type,
    displayWaitListButton,
  } = data;
  const totalCredits = useMemo(() => getTotalCredits(data), [data]);
  const creditsAmount: number = useMemo(
    () => totalCredits?.reduce((prevValue, { count }) => count + prevValue, 0) || 0,
    [totalCredits]
  );
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [sessionsModalOpen, setSessionsModalOpen] = useState<boolean>(false);
  const [waitListModalOpen, setWaitListModalOpen] = useState<boolean>(false);
  const eventAddedToCart = useMemo(() => cart.find(({ eventId }) => eventId === id), [cart, id]);
  const showTimeBox: boolean = useMemo(() => endDate && startDate.isSame(endDate, 'days'), [endDate, startDate]);
  const eventAvailableByMemberType: boolean = useMemo(
    () => !memberTypes?.length || !!memberTypes?.find(({ name }) => name === currentUser.memberTypeName),
    [currentUser, memberTypes]
  );

  const handleAddCourseClick = useCallback(() => {
    if (sessions?.length) {
      setSessionsModalOpen(true);
    } else {
      setSubmitLoading(true);
      EventService.addEventToCart(id)
        .then(() => {
          setSubmitLoading(false);
          enqueueSnackbar('Event was added to cart', {
            variant: 'success',
            anchorOrigin: { horizontal: 'center', vertical: 'top' },
          });
          refetchCart();
        })
        .catch((errorMessage: string) => {
          setSubmitLoading(false);
          enqueueSnackbar(errorMessage, { variant: 'error', anchorOrigin: { horizontal: 'center', vertical: 'top' } });
        });
    }
  }, [sessions, id, enqueueSnackbar, refetchCart]);

  const handleSessionsModalClose = useCallback(() => {
    setSessionsModalOpen(false);
  }, []);

  const handleAddWaitListModalOpen = useCallback(() => {
    setWaitListModalOpen(true);
  }, []);

  const handleAddWaitListModalClose = useCallback(() => {
    setWaitListModalOpen(false);
  }, []);

  return (
    <>
      <section className={styles.banner} style={{ backgroundImage: `url(${imageUrl})` }}>
        <Container maxWidth={'lg'} className={styles.container}>
          <Grid {...defaultGridContainerProps}>
            <Grid {...defaultGridItemProps} className={styles.topInfo}>
              <div className={styles.typeLabel}>
                {getEventModeIcon(mode, styles.typeIcon)}
                {`${eventModeConfig[mode]?.name} | ${type}`}
              </div>
              {!!sessions?.length && <StatusLabel status={multiSessionLabelName}></StatusLabel>}
            </Grid>
            <Grid {...defaultGridItemProps}>
              <h1 className={styles.name}>{title}</h1>
            </Grid>
            <Grid {...defaultGridItemProps} lg={9} xs={12}>
              <Grid {...defaultGridContainerProps} spacing={1}>
                <Grid
                  {...defaultGridItemProps}
                  lg={showTimeBox ? (creditsAmount ? 5 : 6) : true}
                  sm={showTimeBox ? 6 : true}
                >
                  <div className={styles.box}>
                    <EventDateRow data={data} className={styles.eventInfoRow} />
                  </div>
                </Grid>
                {showTimeBox && (
                  <Grid {...defaultGridItemProps} lg={creditsAmount ? 5 : 6} sm={6}>
                    <div className={styles.box}>
                      <EventTimeRow data={data} className={styles.eventInfoRow} />
                    </div>
                  </Grid>
                )}
                {!!creditsAmount && (
                  <Grid {...defaultGridItemProps} lg={2}>
                    <div className={styles.box}>
                      <div className={classNames(rowStyles.row, styles.eventInfoRow)}>
                        <SchoolOutlined className={rowStyles.icon} />
                        {`${creditsAmount} credit(s)`}
                      </div>
                    </div>
                  </Grid>
                )}
                <Grid {...defaultGridItemProps} lg={12}>
                  <div className={styles.box}>
                    <EventLocationRow data={data} className={styles.eventInfoRow} fullLocation={true} />
                  </div>
                </Grid>
              </Grid>
            </Grid>
            <Grid {...defaultGridItemProps} lg={3} xs={12}>
              <Card contentClassName={styles.priceCardContent}>
                <Grid {...defaultGridContainerProps}>
                  {availableForSale ? (
                    currentUser && !eventAvailableByMemberType ? (
                      <Grid {...defaultGridItemProps}>
                        <p className={styles.notAvailableEvent}>
                          {'Product is currently not available for your member type'}
                        </p>
                      </Grid>
                    ) : (
                      <>
                        <Grid {...defaultGridItemProps}>
                          <span className={styles.subtitle}>{'You Pay:'}</span>
                        </Grid>
                        <Grid {...defaultGridItemProps} className={styles.price}>
                          <span>{getPrice(getEventPriceLevel(prices, priceLevelId))}</span>
                        </Grid>
                        <Grid {...defaultGridItemProps}>
                          {hasAuthorizedUser ? (
                            displayWaitListButton ? (
                              <Button
                                variant={'outlined'}
                                color={'secondary'}
                                size={'large'}
                                onClick={handleAddWaitListModalOpen}
                                disabled={submitLoading || cartLoading}
                                {...getButtonLoadingProps(submitLoading || cartLoading)}
                              >
                                {'Join Waitlist'}
                              </Button>
                            ) : eventAddedToCart ? (
                              <Button
                                size={'large'}
                                variant={'outlined'}
                                color={'secondary'}
                                disabled={true}
                                startIcon={<Check />}
                              >
                                {'Added To Cart'}
                              </Button>
                            ) : (
                              <Button
                                variant={'contained'}
                                color={'secondary'}
                                size={'large'}
                                onClick={handleAddCourseClick}
                                disabled={submitLoading || cartLoading}
                                {...getButtonLoadingProps(submitLoading || cartLoading)}
                              >
                                {'Add To Cart'}
                              </Button>
                            )
                          ) : (
                            <>
                              <p className={styles.loginText}>
                                {'Please log in or register to complete your purchase.'}
                              </p>
                              <Button variant={'contained'} color={'secondary'} href={getLoginPath(pathname)}>
                                {'Sign In'}
                              </Button>
                            </>
                          )}
                        </Grid>
                        {availableUntilDate && availableUntilDate.isAfter(new Date()) && (
                          <Grid {...defaultGridItemProps}>
                            <span
                              className={styles.availableUntilDate}
                            >{`Registration for the event is available until ${availableUntilDate.format(
                              EVENT_DATE_FORMAT
                            )}`}</span>
                          </Grid>
                        )}
                      </>
                    )
                  ) : (
                    <Grid {...defaultGridItemProps}>
                      <p className={styles.notAvailableEvent}>{'Registration Unavailable'}</p>
                    </Grid>
                  )}
                </Grid>
              </Card>
              {!!totalCredits?.length && (
                <Card className={styles.additionInfoBox}>
                  <span className={styles.title}>
                    <SchoolOutlined className={rowStyles.icon} />
                    {'Credits: '}
                  </span>
                  {totalCredits.map(({ name, count, id }) => (
                    <span className={styles.value} key={id}>{`${count.toFixed(2)} credit(s) - ${name}`}</span>
                  ))}
                </Card>
              )}
              {/*<Card className={styles.additionInfoBox} contentClassName={styles.speakersBox}>*/}
              {/*  <span className={styles.title}>*/}
              {/*    <SchoolOutlined className={rowStyles.icon} />*/}
              {/*    {'Speaker(s): '}*/}
              {/*  </span>*/}
              {/*  <span className={styles.value}>{'TBD'}</span>*/}
              {/*</Card>*/}
            </Grid>
          </Grid>
        </Container>
      </section>
      <section className={styles.description}>
        <Container maxWidth={'lg'} className={styles.container}>
          <Grid {...defaultGridContainerProps}>
            <Grid {...defaultGridItemProps} sm={8}>
              <p
                className={commonStyles.text}
                dangerouslySetInnerHTML={{ __html: description || EMPTY_STRING_VALUE }}
              />
            </Grid>
            {!!material && (
              <Grid {...defaultGridItemProps}>
                <h4 className={commonStyles.subTitle}>{'Materials:'}</h4>
                <EventFilesSection data={data} />
              </Grid>
            )}
          </Grid>
        </Container>
      </section>
      <ManageEventSessionsModal
        data={sessions || []}
        id={id}
        open={sessionsModalOpen}
        onClose={handleSessionsModalClose}
        mode={ManageCourseSessionsMode.AddToCart}
      />
      <AddToWaitListModal data={data} open={waitListModalOpen} onClose={handleAddWaitListModalClose} />
    </>
  );
};

export default EventDetailsPageView;
